import React, {useState} from 'react';
import {DateTime, DurationObject} from 'luxon';
import {Box, Divider} from '@material-ui/core';

import {School} from '../../../api/types';
import {VisibleEndDateErrorTypes} from '../../../containers/home/utils/end-date-validation';
import {formatAcademicYearsPeriod} from '../../../helpers/format-academic-year-period';
import {EndDatePickerFormControl} from '../../forms/end-date-picker-form-control/end-date-picker-form-control';
import {HighlightHeading, HighlightSection} from '../../modal/components/modal-sections.styled';
import {DurationArrowIcon} from '../../modal/components/duration-arrow-icon';
import {DisplaySection} from '../../modal/components/info-section/display-section';
import {ReadOnlyTextField, SectionHelpText, WarningText} from './change-end-year-view.styled';
import {HTML_INPUT_DATE_FORMAT} from '../../../helpers/utils';
import {HStack, VStack} from '../../layout/layout.styled';
import {HeadingSection} from '../../modal/modal.styled';
import {useEffectAfterMount} from '../../../hooks';
import {DateChangeDifference} from '../academic-year-change-modal';


type Props = {
    school: School
    onEndDateChange: (endDateTime: DateTime) => any
    endDate: DateTime | null
    dateChangeDifference: DateChangeDifference
    isAcademicYear: boolean,
    isAcademicYearLess: boolean
}

type EndDateWarningCase = {
    warningType: keyof WarningCases
    message1: string
    message2: string
    isFutureAcademicYearPresent: boolean
}
type WarningCases = {
    warningTitle: EndDateWarningCase
    waringText: EndDateWarningCase
    futureAcademicYearWarningTitle: EndDateWarningCase
    futureAcademicYearWaringText: EndDateWarningCase
}

const EndDateWarningTypes: WarningCases = {
    warningTitle: {
        warningType: 'warningTitle',
        message1: 'Please ensure this is correct',
        message2: '',
        isFutureAcademicYearPresent: false
    },
    waringText: {
        warningType: 'waringText',
        message1: 'You are about to set the current academic year to ',
        message2: ' than 1 standard year',
        isFutureAcademicYearPresent: false
    },
    futureAcademicYearWarningTitle: {
        warningType: 'futureAcademicYearWarningTitle',
        message1: 'Cannot change the academic year.',
        message2: '',
        isFutureAcademicYearPresent: true
    },
    futureAcademicYearWaringText: {
        warningType: 'futureAcademicYearWaringText',
        message1: 'The next academic year for this school has already been created.',
        message2: 'The current academic year cannot be changed.',
        isFutureAcademicYearPresent: true
    }
};

const VisibleEndDateWarningTypes: WarningCases = {
    warningTitle: EndDateWarningTypes.warningTitle,
    waringText: EndDateWarningTypes.waringText,
    futureAcademicYearWarningTitle: EndDateWarningTypes.futureAcademicYearWarningTitle,
    futureAcademicYearWaringText: EndDateWarningTypes.futureAcademicYearWaringText
};

export const ChangeEndYearView = ({
    school,
    endDate,
    onEndDateChange,
    dateChangeDifference,
    isAcademicYear,
    isAcademicYearLess
}: Props) => {
    const startDateTime = DateTime.fromISO(school?.academicYear?.startDate || '', {zone: 'utc'});
    const startDate = startDateTime.toFormat(HTML_INPUT_DATE_FORMAT);
    const defaultEndDateTime = DateTime.fromISO(school?.academicYear?.endDate || '', {zone: 'utc'});
    const defaultEndDate = defaultEndDateTime.toFormat(HTML_INPUT_DATE_FORMAT);
    const academicYearName = formatAcademicYearsPeriod(school.academicYear || {});
    const [isWarningVisible, setIsWarningVisible] = useState(false);
    useEffectAfterMount(() => {
        setIsWarningVisible(true);
    }, [endDate]);

    return (
        <VStack>
            <HeadingSection
                headingText={'School'}
                labelText={`${school?.name} (${school.crmId})`}
            />

            <HStack>
                <HeadingSection
                    headingText={'Academic Year name'}
                    labelText={academicYearName}
                />

                <Box display={'flex'} py={'15px'}>
                    <Divider orientation="vertical" flexItem/>
                </Box>

                <HeadingSection
                    headingText={'Region or School setting'}
                    labelText={`${school?.academicYear?.isSchoolAcademicYear ? 'School' : 'Region'}`}
                />
            </HStack>

            <HighlightSection spacing={'30px'}>
                <div>
                    <HighlightHeading>
                        Change the current Academic Year End Date
                    </HighlightHeading>

                    <HStack spacing={'30px'} alignItems={'center'}>
                        <ReadOnlyTextField
                            label="Start"
                            type="string"
                            defaultValue={startDate}
                            InputProps={{
                                readOnly: true
                            }}
                            InputLabelProps={{
                                disableAnimation: true,
                                shrink: true
                            }}
                        />

                        <DurationArrowIcon/>

                        {
                            school.hasFutureSchoolAcademicYear ?
                                <ReadOnlyTextField
                                    label="End"
                                    type="string"
                                    defaultValue={defaultEndDate}
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    InputLabelProps={{
                                        disableAnimation: true,
                                        shrink: true
                                    }}
                                /> : <EndDatePickerFormControl
                                    data-testid={'EndDateInput'}
                                    labelText="End"
                                    startDate={startDateTime}
                                    onChange={onEndDateChange}
                                    initialValue={endDate}
                                />
                        }
                    </HStack>
                </div>
                {isWarningVisible && !isAcademicYear
                    ? <DisplaySection variant={'warning'}>
                        {Object.values(VisibleEndDateWarningTypes)
                            .filter(warning => !warning.isFutureAcademicYearPresent)
                            .map(warning =>
                                <SectionHelpText displayWarning key={warning.warningType}>
                                    {warning.warningType === 'warningTitle'
                                        ? <span data-testid={'warningTitle'}>{warning.message1}</span>
                                        : <WarningText>
                                            <span>
                                                {warning.message1}
                                                {isAcademicYearLess ? 'less' : 'more'}
                                                {warning.message2}
                                            </span>
                                            <SectionHelpText>
                                                <strong>{createWarningText(dateChangeDifference)}</strong>
                                            </SectionHelpText>
                                        </WarningText>
                                    }
                                </SectionHelpText>
                            )}
                    </DisplaySection>
                    : ''
                }
                { school.hasFutureSchoolAcademicYear
                    ? <DisplaySection variant={'warning'} data-testid={'hasFutureSchoolAcademicYear'}>
                        {Object.values(EndDateWarningTypes).filter(warning => warning.isFutureAcademicYearPresent)
                            .map(warning =>
                                <SectionHelpText displayWarning key={warning.warningType}>
                                    {warning.warningType === 'futureAcademicYearWarningTitle'
                                        ? <span data-testid={'futureAcademicYearWarningTitle'}>
                                            {warning.message1}
                                        </span>
                                        : <WarningText>
                                            {warning.message1}
                                            {warning.message2}
                                        </WarningText>
                                    }
                                </SectionHelpText>

                            )}
                    </DisplaySection>
                    : ''
                }
            </HighlightSection>

            <DisplaySection variant={'info'}>
                {Object.values(VisibleEndDateErrorTypes).map(error =>
                    <SectionHelpText info key={error.errorType} data-testid={'infoTitle'}>
                        * {error.message}
                    </SectionHelpText>
                )}
            </DisplaySection>
        </VStack>
    );
}
;

function isDateDifferenceObject(value: any): value is DurationObject {
    return (
        value &&
        typeof value?.years === 'number' &&
        typeof value?.months === 'number' &&
        typeof value?.weeks === 'number'
    )
}

function createWarningText(value: DateChangeDifference) {
    if (isDateDifferenceObject(value)) {
        return `${value.years} years,
        ${value.months} months,
        ${Math.ceil(value.weeks as number)} weeks`
    } else return null
}
